import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertCircle, X, Trash2 } from "lucide-react";
import EngineerLayout from "../../layouts/EngineerLayout";
import API from "../../services/api";
import {
  Card,
  SectionHeader,
} from "../../components/dashboard/DashboardKit";

export default function EngineerStakeholders() {
  const { id: urlProjectId } = useParams();
  const navigate              = useNavigate();
  const [stakeholders, setStakeholders]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState("");
  const [currentProject, setCurrentProject] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [modal, setModal]       = useState(null);
  const [formData, setFormData] = useState({
    name:        "",
    designation: "",
    phone:       "",
    email:       "",
  });

  // ── ROOT CAUSE FIX ──────────────────────────────────────────────────────────
  // The DB screenshot (query #92) shows master_stakeholders returns 0 rows —
  // the table exists and the schema is correct (id, name, designation, phone,
  // email, project_id, is_deleted, created_at). No data has been added yet,
  // so the empty state is correct behaviour and the fetch path is right.
  //
  // Bug 1 — DELETE endpoint mismatch.
  // handleDelete called DELETE /engineers/me/stakeholders/:id
  // but the POST used     POST  /engineers/me/projects/:projectId/stakeholders
  // These are inconsistent. The backend almost certainly expects the project-
  // scoped path for delete as well:
  //   DELETE /engineers/me/projects/:projectId/stakeholders/:stakeholderId
  // Fix: pass urlProjectId in the delete path.
  //
  // Bug 2 — Response key tolerance.
  // The backend may return res.data.data or res.data.stakeholders depending on
  // the route handler. The original code only checked res.data.stakeholders.
  // Fix: fall back to res.data.data so either shape works.
  //
  // Bug 3 — handleSave called e.preventDefault() but was also wired as a
  // direct onClick on a submit button inside a <form onSubmit={handleSave}>.
  // This is fine as-is; kept unchanged.
  //
  // Everything else (layout, styling, class names, form fields) is unchanged.
  // ────────────────────────────────────────────────────────────────────────────

  const fetchStakeholders = async () => {
    if (!urlProjectId) return;
    setLoading(true);
    try {
      const res = await API.get(`/engineers/me/projects/${urlProjectId}/stakeholders`);
      if (res.data?.success) {
        // Accept either response shape from the backend
        setStakeholders(res.data.stakeholders || res.data.data || []);
      } else {
        setStakeholders([]);
      }
    } catch (err) {
      console.error("Error fetching stakeholders:", err);
      setStakeholders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlProjectId) {
      API.get(`/projects/${urlProjectId}`)
        .then(res => {
          if (res.data?.success) setCurrentProject(res.data.project);
        })
        .catch(err => console.error("Error fetching project:", err));

      fetchStakeholders();
    }
  }, [urlProjectId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === "phone") {
      // Limit to max 10 numeric digits only
      val = value.replace(/\D/g, "").slice(0, 10);
    }
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const designation = formData.designation.trim();

    if (!name || !email || !phone) {
      setModal({
        type: "error",
        title: "Missing Information",
        message: "Please fill in all required fields (Name, Email, and Phone).",
      });
      return;
    }

    if (phone.length !== 10) {
      setModal({
        type: "error",
        title: "Invalid Phone Number",
        message: "Please enter a valid 10-digit mobile number.",
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setModal({
        type: "error",
        title: "Invalid Email Address",
        message: "Please enter a valid email address.",
      });
      return;
    }

    try {
      setSaving(true);
      const res = await API.post(
        `/engineers/me/projects/${urlProjectId}/stakeholders`,
        {
          name,
          email,
          phone,
          designation,
        }
      );
      if (res.data?.success) {
        setModal({
          type: "success",
          title: "Added Successfully!",
          message: `${name} has been added as ${designation || "Stakeholder"} to this project.`,
        });
        setFormData({ name: "", designation: "", phone: "", email: "" });
        setShowForm(false);
        fetchStakeholders();
      } else {
        setModal({
          type: "error",
          title: "Failed to Add",
          message: res.data?.message || "Could not add stakeholder.",
        });
      }
    } catch (err) {
      setModal({
        type: "error",
        title: "Failed to Add",
        message: err.response?.data?.message || "Error adding stakeholder. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (stakeholderId, stakeholderName) => {
    setModal({
      type: "confirm",
      title: "Remove Stakeholder?",
      message: `Are you sure you want to remove ${stakeholderName || "this stakeholder"} from this project?`,
      onConfirm: async () => {
        try {
          const res = await API.delete(
            `/engineers/me/projects/${urlProjectId}/stakeholders/${stakeholderId}`
          );
          if (res.data?.success) {
            setModal({
              type: "success",
              title: "Removed Successfully",
              message: "Stakeholder has been removed from this project.",
            });
            fetchStakeholders();
          } else {
            setModal({
              type: "error",
              title: "Failed to Remove",
              message: res.data?.message || "Could not remove stakeholder.",
            });
          }
        } catch (err) {
          setModal({
            type: "error",
            title: "Failed to Remove",
            message: err.response?.data?.message || "Error deleting stakeholder.",
          });
        }
      },
    });
  };

  const filteredStakeholders = stakeholders.filter(
    (s) =>
      (s.name        || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.designation || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.email       || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
<EngineerLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap'); * { font-family: 'Montserrat', sans-serif !important; }`}</style>
      <div className="grid gap-3.5">
        <div>
          <button
            onClick={() => navigate(`/engineer/project/${urlProjectId}`)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
              border: "1.5px solid #E2E8F0",
              borderRadius: 8,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              color: "#5C2D91",
              cursor: "pointer",
              fontFamily: "'Montserrat', sans-serif",
              marginBottom: 4,
            }}
          >
            ← Back to Project
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
           <h1 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 24, margin: 0, color: "#1A0A2E" }}>Project Stakeholders</h1>
            <p style={{ fontSize: 12, color: "#6B5A8A", marginTop: 4 }}>
              {currentProject ? `Stakeholders for project: ${currentProject.title}` : "Manage stakeholders and contacts"}
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              background: "linear-gradient(135deg,#5C2D91,#8054B8)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(92,45,145,0.2)",
            }}
          >
            {showForm ? "Close Form" : "+ Add Stakeholder"}
          </button>
        </div>

        {/* Stakeholder Add Form */}
        {showForm && (
          <Card>
            <div className="p-4 sm:p-5">
              <h3 className="text-md font-bold text-ap-dark mb-4">Add Project Stakeholder</h3>
              <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-ap-muted mb-1">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Rajesh Kumar"
                    className="w-full rounded-lg border border-ap-border bg-ap-surfaceAlt px-3 py-2 text-sm text-ap-dark outline-none focus:border-ap-violet"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ap-muted mb-1">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="e.g. Project Manager"
                    className="w-full rounded-lg border border-ap-border bg-ap-surfaceAlt px-3 py-2 text-sm text-ap-dark outline-none focus:border-ap-violet"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ap-muted mb-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. rajesh.kumar@corp.com"
                    className="w-full rounded-lg border border-ap-border bg-ap-surfaceAlt px-3 py-2 text-sm text-ap-dark outline-none focus:border-ap-violet"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ap-muted mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={10}
                    placeholder="Enter 10-digit mobile number"
                    className="w-full rounded-lg border border-ap-border bg-ap-surfaceAlt px-3 py-2 text-sm text-ap-dark outline-none focus:border-ap-violet"
                    required
                  />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="rounded-lg border border-ap-border px-4 py-2 text-xs font-bold text-ap-muted hover:bg-ap-surfaceAlt"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-ap-violet px-4 py-2 text-xs font-bold text-white hover:bg-ap-violet/90 disabled:opacity-60"
                  >
                    {saving ? "Adding..." : "Add Stakeholder"}
                  </button>
                </div>
              </form>
            </div>
          </Card>
        )}

        <Card>
          <div className="p-4 sm:p-5">
            <SectionHeader
              title="Project Contacts"
              subtitle={`${filteredStakeholders.length} stakeholder${filteredStakeholders.length === 1 ? "" : "s"}`}
            />

            <div className="mb-4 mt-4">
              <input
                type="text"
                placeholder="Search by name, role, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-ap-border bg-ap-surfaceAlt px-4 py-2.5 text-sm text-ap-dark outline-none transition focus:border-ap-violet"
              />
            </div>

            {loading && (
              <div className="py-10 text-center text-sm text-ap-muted">
                Loading project stakeholders...
              </div>
            )}

            {!loading && filteredStakeholders.length === 0 && (
              <div className="py-10 text-center text-sm text-ap-muted">
                No stakeholders registered for this project. Click <strong>+ Add Stakeholder</strong> to add one.
              </div>
            )}

            {!loading && filteredStakeholders.length > 0 && (
              <div className="overflow-x-auto">
                <div className="min-w-[900px] space-y-2">
                  {filteredStakeholders.map((stakeholder) => {
                    const initials = (stakeholder.name || "S")
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <div
                        key={stakeholder.id}
                        className="grid grid-cols-[50px_200px_150px_180px_180px_auto] gap-4 items-center rounded-[12px] bg-ap-surfaceAlt p-4 transition hover:bg-ap-violet/10"
                      >
                        <div style={{
                          width: 38, height: 38, borderRadius: "50%",
                          background: "#EDE6F7", display: "flex", alignItems: "center",
                          justifyContent: "center", fontWeight: 700, fontSize: 13, color: "#5C2D91",
                        }}>
                          {initials}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-ap-dark">{stakeholder.name}</div>
                          <div className="text-[10px] text-ap-muted">{stakeholder.designation || "Stakeholder"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-ap-muted">ROLE</div>
                          <div className="text-xs font-semibold text-ap-dark">{stakeholder.designation || "External Client"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-ap-muted">EMAIL</div>
                          <div className="text-[11px] text-ap-dark truncate">{stakeholder.email}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold text-ap-muted">PHONE</div>
                          <div className="text-xs text-ap-dark">{stakeholder.phone}</div>
                        </div>
<div className="flex justify-end gap-2">
                          {!stakeholder.is_applicator && (
                            <button
                              onClick={() => handleDelete(stakeholder.id, stakeholder.name)}
                              className="bg-ap-red/10 text-ap-red hover:bg-ap-red hover:text-white border border-ap-red/20 rounded-lg px-3 py-1.5 text-xs font-bold transition"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── Custom Asian Paints UI Modal Box (Replaces native browser alert) ── */}
      {modal && (
        <div
          onClick={() => setModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(26, 10, 46, 0.52)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            animation: "modalFadeIn 0.18s ease-out",
          }}
        >
          <style>{`
            @keyframes modalFadeIn {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 24px 64px rgba(92, 45, 145, 0.24), 0 4px 20px rgba(0,0,0,0.08)",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Top Accent Gradient Bar */}
            <div
              style={{
                height: 5,
                background:
                  modal.type === "success"
                    ? "linear-gradient(90deg, #5C2D91, #10B981)"
                    : modal.type === "confirm"
                    ? "linear-gradient(90deg, #5C2D91, #F59E0B)"
                    : "linear-gradient(90deg, #5C2D91, #EF4444)",
              }}
            />

            {/* Close Button */}
            <button
              onClick={() => setModal(null)}
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                background: "#F8F5FC",
                border: "none",
                borderRadius: "50%",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#6B5A8A",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#EDE6F7"; e.currentTarget.style.color = "#1A0A2E"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#F8F5FC"; e.currentTarget.style.color = "#6B5A8A"; }}
            >
              <X size={16} />
            </button>

            <div style={{ padding: "28px 24px 22px", textAlign: "center" }}>
              {/* Icon Circle */}
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    modal.type === "success"
                      ? "#ECFDF5"
                      : modal.type === "confirm"
                      ? "#FFFBEB"
                      : "#FEF2F2",
                  border:
                    modal.type === "success"
                      ? "2px solid #A7F3D0"
                      : modal.type === "confirm"
                      ? "2px solid #FDE68A"
                      : "2px solid #FECDD3",
                }}
              >
                {modal.type === "success" && <CheckCircle2 size={30} color="#059669" />}
                {modal.type === "confirm" && <Trash2 size={26} color="#D97706" />}
                {modal.type === "error" && <AlertCircle size={30} color="#DC2626" />}
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#1A0A2E",
                  margin: "0 0 8px",
                  letterSpacing: "-0.2px",
                }}
              >
                {modal.title}
              </h3>

              {/* Message */}
              <p
                style={{
                  fontSize: 13,
                  color: "#6B5A8A",
                  margin: "0 0 24px",
                  lineHeight: 1.6,
                }}
              >
                {modal.message}
              </p>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                {modal.type === "confirm" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setModal(null)}
                      style={{
                        flex: 1,
                        padding: "10px 18px",
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 600,
                        background: "#F1F5F9",
                        color: "#64748B",
                        border: "1px solid #CBD5E1",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const cb = modal.onConfirm;
                        setModal(null);
                        if (cb) cb();
                      }}
                      style={{
                        flex: 1,
                        padding: "10px 18px",
                        borderRadius: 12,
                        fontSize: 13,
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #EF4444, #DC2626)",
                        color: "#fff",
                        border: "none",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                      }}
                    >
                      Yes, Remove
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setModal(null)}
                    style={{
                      minWidth: 130,
                      padding: "11px 28px",
                      borderRadius: 12,
                      fontSize: 13,
                      fontWeight: 700,
                      background:
                        modal.type === "success"
                          ? "linear-gradient(135deg, #059669, #10B981)"
                          : "linear-gradient(135deg, #5C2D91, #8054B8)",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                      boxShadow:
                        modal.type === "success"
                          ? "0 4px 14px rgba(16, 185, 129, 0.35)"
                          : "0 4px 14px rgba(92, 45, 145, 0.35)",
                    }}
                  >
                    OK
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </EngineerLayout>
  );
}